import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  DollarSign,
  Search,
  Filter,
  Trash2,
  Calendar,
  CreditCard,
  RefreshCcw,
  ArrowUpDown,
  Pencil,
  X,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../hooks/useCurrency';
import { useTitle } from '../hooks/useTitle';
import { customToast } from '../utils/customToast';
import { format } from 'date-fns';
import { shopService } from '../services/shopService';

interface Payment {
  id: number;
  shop_id: number;
  amount: string;
  payment_date: string;
  payment_method: string;
  reference: string | null;
  subscription_months: number;
  subscription_starts_at: string;
  subscription_ends_at: string;
  notes: string | null;
  recorded_by: number;
  created_at: string;
  updated_at: string;
  recorded_by_data?: {
    id: number;
    username: string;
    email: string;
  };
  shop?: {
    id: number;
    shop_name: string;
    owner_name: string;
    owner_email: string;
  };
}

const PaymentManagement: React.FC = () => {
  const { theme } = useTheme();
  const { formatCurrency } = useCurrency();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMethod, setFilterMethod] = useState('all');
  const [sortBy, setSortBy] = useState<'date' | 'amount'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useTitle('Payment Management');

  useEffect(() => {
    fetchAllPayments();
  }, []);

  const fetchAllPayments = async () => {
    try {
      setLoading(true);
      // Fetch all shops and their payment histories
      const shopsResponse = await shopService.getShops({ per_page: 1000 });
      
      // Debug: Log the response structure
      console.log('Shops response:', shopsResponse);
      
      const allPayments: Payment[] = [];

      // Handle different response structures
      let shops: any[] = [];
      if (Array.isArray(shopsResponse)) {
        shops = shopsResponse;
      } else if (shopsResponse && Array.isArray(shopsResponse.data)) {
        shops = shopsResponse.data;
      } else if (shopsResponse && (shopsResponse as any).data && Array.isArray((shopsResponse as any).data.data)) {
        shops = (shopsResponse as any).data.data;
      }

      if (shops.length === 0) {
        console.warn('No shops found');
        setPayments([]);
        return;
      }

      console.log(`Found ${shops.length} shops, fetching payment histories...`);

      for (const shop of shops) {
        try {
          const paymentHistory = await shopService.getPaymentHistory(shop.id);
          const paymentsWithShop = paymentHistory.map((payment: any) => ({
            ...payment,
            shop: {
              id: shop.id,
              shop_name: shop.shop_name,
              owner_name: shop.owner_name,
              owner_email: shop.owner_email,
            },
            recorded_by_data: payment.recorded_by,
          }));
          allPayments.push(...paymentsWithShop);
        } catch (error) {
          console.error(`Error fetching payments for shop ${shop.id}:`, error);
        }
      }

      console.log(`Total payments fetched: ${allPayments.length}`);
      setPayments(allPayments);
    } catch (error) {
      console.error('Error fetching payments:', error);
      customToast.error('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePayment = async (shopId: number, paymentId: number) => {
    if (!window.confirm('Are you sure you want to delete this payment? This action cannot be undone.')) {
      return;
    }

    try {
      await shopService.deletePayment(shopId, paymentId);
      customToast.success('Payment deleted successfully');
      fetchAllPayments();
    } catch (error) {
      console.error('Error deleting payment:', error);
      customToast.error('Failed to delete payment');
    }
  };

  const handleEditPayment = async (updatedPayment: Partial<Payment>) => {
    if (!editingPayment) return;

    try {
      // Convert the payment data to match the API interface
      const paymentData = {
        amount: parseFloat(updatedPayment.amount || '0'),
        payment_date: updatedPayment.payment_date,
        payment_method: updatedPayment.payment_method,
        reference: updatedPayment.reference || '',
        subscription_months: parseInt(updatedPayment.subscription_months?.toString() || '0'),
        notes: updatedPayment.notes || '',
      };

      console.log('Sending payment data:', paymentData); // Debug log
      await shopService.updatePayment(editingPayment.shop_id, editingPayment.id, paymentData);
      customToast.success('Payment updated successfully');
      setShowEditModal(false);
      setEditingPayment(null);
      fetchAllPayments();
    } catch (error) {
      console.error('Error updating payment:', error);
      customToast.error('Failed to update payment');
    }
  };

  const openEditModal = (payment: Payment) => {
    setEditingPayment(payment);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingPayment(null);
  };

  const getFilteredPayments = () => {
    let filtered = [...payments];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (payment) =>
          payment.shop?.shop_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          payment.shop?.owner_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          payment.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          payment.payment_method.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Method filter
    if (filterMethod !== 'all') {
      filtered = filtered.filter((payment) => payment.payment_method === filterMethod);
    }

    // Sort
    filtered.sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'date') {
        comparison = new Date(a.payment_date).getTime() - new Date(b.payment_date).getTime();
      } else {
        comparison = parseFloat(a.amount) - parseFloat(b.amount);
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  };

  const filteredPayments = getFilteredPayments();

  const totalEarnings = payments.reduce((sum, payment) => sum + parseFloat(payment.amount), 0);
  const thisMonthEarnings = payments
    .filter((p) => {
      const paymentDate = new Date(p.payment_date);
      const now = new Date();
      return paymentDate.getMonth() === now.getMonth() && paymentDate.getFullYear() === now.getFullYear();
    })
    .reduce((sum, payment) => sum + parseFloat(payment.amount), 0);

  const paymentMethods = ['all', 'cash', 'bank_transfer', 'credit_card', 'debit_card', 'cheque', 'online'];

  // Edit Payment Modal Component
  const EditPaymentModal = () => {
    const [formData, setFormData] = useState({
      amount: '',
      payment_method: '',
      payment_date: '',
      reference: '',
      notes: '',
      subscription_months: '',
    });
    const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});

    useEffect(() => {
      if (editingPayment) {
        setFormData({
          amount: editingPayment.amount,
          payment_method: editingPayment.payment_method,
          payment_date: editingPayment.payment_date.split('T')[0], // Convert to YYYY-MM-DD format
          reference: editingPayment.reference || '',
          notes: editingPayment.notes || '',
          subscription_months: editingPayment.subscription_months.toString(),
        });
        setFormErrors({});
      }
    }, [editingPayment]);

    const validateForm = () => {
      const errors: {[key: string]: string} = {};
      
      if (!formData.amount || parseFloat(formData.amount) <= 0) {
        errors.amount = 'Amount must be a positive number';
      }
      
      if (!formData.payment_method) {
        errors.payment_method = 'Payment method is required';
      }
      
      if (!formData.payment_date) {
        errors.payment_date = 'Payment date is required';
      }

      if (!formData.subscription_months || parseInt(formData.subscription_months) < 1 || parseInt(formData.subscription_months) > 24) {
        errors.subscription_months = 'Subscription months must be between 1 and 24';
      }

      setFormErrors(errors);
      return Object.keys(errors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (validateForm()) {
        handleEditPayment({
          amount: formData.amount,
          payment_method: formData.payment_method,
          payment_date: formData.payment_date,
          reference: formData.reference || null,
          notes: formData.notes || null,
          subscription_months: parseInt(formData.subscription_months),
        });
      }
    };

    if (!showEditModal || !editingPayment) return null;

    return createPortal(
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50"
        onClick={(e) => e.target === e.currentTarget && closeEditModal()}
      >
        <div 
          className={`w-full max-w-2xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl ${
            theme === 'dark' ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-gray-200'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
            {/* Header */}
            <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b rounded-t-xl flex-shrink-0 ${
              theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                  <div className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${theme === 'dark' ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                    <CreditCard className={`w-4 h-4 sm:w-5 sm:h-5 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-600'}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base sm:text-lg font-semibold truncate">Edit Payment</h3>
                    <p className={`text-xs sm:text-sm truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      {editingPayment.shop?.shop_name}
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeEditModal}
                  className={`p-1.5 sm:p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors touch-manipulation flex-shrink-0 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-4 sm:px-5 md:px-6 py-4 sm:py-5 flex-1 overflow-y-auto">
              <form id="edit-payment-form" onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                {/* Responsive grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {/* Left Column */}
                  <div className="space-y-3 sm:space-y-4">
                    <div>
                      <label className={`block text-xs sm:text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Amount *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        className={`w-full px-3 py-2.5 sm:py-2 border rounded-lg text-sm touch-manipulation ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        } ${formErrors.amount ? 'border-red-500' : ''} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                      {formErrors.amount && <p className="text-red-500 text-xs mt-1">{formErrors.amount}</p>}
                    </div>

                    <div>
                      <label className={`block text-xs sm:text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Payment Method *
                      </label>
                      <select
                        value={formData.payment_method}
                        onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                        className={`w-full px-3 py-2.5 sm:py-2 border rounded-lg text-sm touch-manipulation ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        } ${formErrors.payment_method ? 'border-red-500' : ''} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      >
                        <option value="">Select payment method</option>
                        {paymentMethods.filter(method => method !== 'all').map((method) => (
                          <option key={method} value={method}>
                            {method.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </option>
                        ))}
                      </select>
                      {formErrors.payment_method && <p className="text-red-500 text-xs mt-1">{formErrors.payment_method}</p>}
                    </div>

                    <div>
                      <label className={`block text-xs sm:text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Payment Date *
                      </label>
                      <input
                        type="date"
                        value={formData.payment_date}
                        onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                        className={`w-full px-3 py-2.5 sm:py-2 border rounded-lg text-sm touch-manipulation ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        } ${formErrors.payment_date ? 'border-red-500' : ''} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                      {formErrors.payment_date && <p className="text-red-500 text-xs mt-1">{formErrors.payment_date}</p>}
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Subscription Months *
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="24"
                        value={formData.subscription_months}
                        onChange={(e) => setFormData({ ...formData, subscription_months: e.target.value })}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        } ${formErrors.subscription_months ? 'border-red-500' : ''} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        placeholder="Number of months (1-24)"
                      />
                      {formErrors.subscription_months && <p className="text-red-500 text-xs mt-1">{formErrors.subscription_months}</p>}
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Reference
                      </label>
                      <input
                        type="text"
                        value={formData.reference}
                        onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        placeholder="Payment reference (optional)"
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Notes
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows={3}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        placeholder="Additional notes (optional)"
                      />
                    </div>
                  </div>
                </div>
              </form>
            </div>

            {/* Footer */}
            <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-t rounded-b-xl flex-shrink-0 ${
              theme === 'dark' ? 'border-gray-700 bg-slate-800/95' : 'border-gray-200 bg-gray-50/95'
            }`}>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className={`px-4 sm:px-5 md:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-medium rounded-xl border transition-all duration-200 shadow-lg hover:shadow-xl touch-manipulation ${
                    theme === 'dark'
                      ? 'border-gray-600 text-gray-300 hover:bg-slate-700 hover:border-gray-500'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="edit-payment-form"
                  className="px-4 sm:px-5 md:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-medium rounded-xl bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200 shadow-lg hover:shadow-xl touch-manipulation"
                >
                  Update Payment
                </button>
              </div>
            </div>
          </div>
      </div>,
      document.body
    );
  };

  return (
    <div className={`min-h-screen px-2.5 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4 md:py-6 ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="min-w-0 flex-1">
              <h1 className={`text-xl sm:text-2xl md:text-3xl font-bold truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Payment Management
              </h1>
              <p className={`text-xs sm:text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                View and manage all shop payments
              </p>
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={fetchAllPayments}
                disabled={loading}
                className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors touch-manipulation w-full sm:w-auto ${
                  theme === 'dark'
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                } disabled:opacity-50`}
              >
                <RefreshCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className={`p-3 sm:p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow`}>
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Total Earnings
                  </p>
                  <p className={`text-lg sm:text-xl lg:text-2xl font-bold truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {formatCurrency(totalEarnings)}
                  </p>
                </div>
                <DollarSign className={`h-6 w-6 sm:h-8 sm:w-8 flex-shrink-0 ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`} />
              </div>
            </div>
            <div className={`p-3 sm:p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow`}>
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    This Month
                  </p>
                  <p className={`text-lg sm:text-xl lg:text-2xl font-bold truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {formatCurrency(thisMonthEarnings)}
                  </p>
                </div>
                <Calendar className={`h-6 w-6 sm:h-8 sm:w-8 flex-shrink-0 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
            </div>
            <div className={`p-3 sm:p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow sm:col-span-2 lg:col-span-1`}>
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Total Payments
                  </p>
                  <p className={`text-lg sm:text-xl lg:text-2xl font-bold truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {payments.length}
                  </p>
                </div>
                <CreditCard className={`h-6 w-6 sm:h-8 sm:w-8 flex-shrink-0 ${theme === 'dark' ? 'text-purple-400' : 'text-purple-600'}`} />
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {/* Search */}
            <div className="relative lg:col-span-1">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <input
                type="text"
                placeholder="Search payments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2.5 sm:py-2 rounded-lg border text-sm touch-manipulation ${
                  theme === 'dark'
                    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
            </div>

            {/* Payment Method Filter */}
            <div className="relative">
              <Filter className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <select
                value={filterMethod}
                onChange={(e) => setFilterMethod(e.target.value)}
                className={`w-full pl-10 pr-4 py-2.5 sm:py-2 rounded-lg border text-sm touch-manipulation ${
                  theme === 'dark'
                    ? 'bg-gray-800 border-gray-700 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                {paymentMethods.map((method) => (
                  <option key={method} value={method}>
                    {method === 'all' ? 'All Methods' : method.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div className="relative sm:col-span-2 lg:col-span-1">
              <ArrowUpDown className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [by, order] = e.target.value.split('-');
                  setSortBy(by as 'date' | 'amount');
                  setSortOrder(order as 'asc' | 'desc');
                }}
                className={`w-full pl-10 pr-4 py-2.5 sm:py-2 rounded-lg border text-sm touch-manipulation ${
                  theme === 'dark'
                    ? 'bg-gray-800 border-gray-700 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                <option value="date-desc">Newest First</option>
                <option value="date-asc">Oldest First</option>
                <option value="amount-desc">Highest Amount</option>
                <option value="amount-asc">Lowest Amount</option>
              </select>
            </div>
          </div>
        </div>

        {/* Payments Table/Cards */}
        <div className={`rounded-lg shadow overflow-hidden ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
          {loading ? (
            <div className="p-8 sm:p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Loading payment history...
              </p>
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className={`h-12 w-12 mx-auto mb-3 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
              <p className={`text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                No payments found
              </p>
            </div>
          ) : (
            <>
              {/* Mobile Cards View (hidden on md and up) */}
              <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
                {filteredPayments.map((payment) => (
                  <div key={`${payment.shop_id}-${payment.id}`} className="p-4 space-y-3">
                    {/* Header with shop and amount */}
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <h3 className={`font-medium truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {payment.shop?.shop_name}
                        </h3>
                        <p className={`text-sm truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          {payment.shop?.owner_name}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0 ml-3">
                        <span className={`text-lg font-semibold ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                          {formatCurrency(parseFloat(payment.amount))}
                        </span>
                      </div>
                    </div>

                    {/* Payment details */}
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className={`block text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          Date
                        </span>
                        <span className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          {format(new Date(payment.payment_date), 'MMM dd, yyyy')}
                        </span>
                      </div>
                      <div>
                        <span className={`block text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          Method
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                        }`}>
                          {payment.payment_method.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </div>
                      <div>
                        <span className={`block text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          Period
                        </span>
                        <div className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          {format(new Date(payment.subscription_starts_at), 'MMM dd, yy')} →
                          <br />
                          {format(new Date(payment.subscription_ends_at), 'MMM dd, yy')}
                        </div>
                      </div>
                      <div>
                        <span className={`block text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          Reference
                        </span>
                        <span className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          {payment.reference || '-'}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => openEditModal(payment)}
                        className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors touch-manipulation ${
                          theme === 'dark' 
                            ? 'bg-blue-900/30 text-blue-400 hover:bg-blue-900/50' 
                            : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                        }`}
                      >
                        <Pencil className="h-3 w-3" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeletePayment(payment.shop_id, payment.id)}
                        className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors touch-manipulation ${
                          theme === 'dark' 
                            ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50' 
                            : 'bg-red-50 text-red-600 hover:bg-red-100'
                        }`}
                      >
                        <Trash2 className="h-3 w-3" />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View (hidden on sm and below) */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <tr>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Shop
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Date
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Amount
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Method
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Period
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Reference
                      </th>
                      <th className={`px-4 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {filteredPayments.map((payment) => (
                      <tr key={`${payment.shop_id}-${payment.id}`} className={`${
                        theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                      } transition-colors`}>
                        <td className="px-4 py-3">
                          <div>
                            <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              {payment.shop?.shop_name}
                            </p>
                            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                              {payment.shop?.owner_name}
                            </p>
                          </div>
                        </td>
                        <td className={`px-4 py-3 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          {format(new Date(payment.payment_date), 'MMM dd, yyyy')}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`font-semibold ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                            {formatCurrency(parseFloat(payment.amount))}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs ${
                            theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                          }`}>
                            {payment.payment_method.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        </td>
                        <td className={`px-4 py-3 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          <div>
                            {format(new Date(payment.subscription_starts_at), 'MMM dd, yy')} →
                          </div>
                          <div>
                            {format(new Date(payment.subscription_ends_at), 'MMM dd, yy')}
                          </div>
                        </td>
                        <td className={`px-4 py-3 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          {payment.reference || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openEditModal(payment)}
                              className={`p-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors ${
                                theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                              }`}
                              title="Edit payment"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeletePayment(payment.shop_id, payment.id)}
                              className={`p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors ${
                                theme === 'dark' ? 'text-red-400' : 'text-red-600'
                              }`}
                              title="Delete payment"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Edit Payment Modal */}
      <EditPaymentModal />
    </div>
  );
};

export default PaymentManagement;
